import * as Field from '@/components/ui/field'
import { X } from 'lucide-solid'
import { For, type JSX } from 'solid-js'
import { segButton, segTrack } from 'src/routes/Setups/List/Header/segStyles'
import { css } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'

/** Titled group inside a page's filter drawer */
export const FilterSection = (props: { title: string; children: JSX.Element }) => (
  <Grid gap={3}>
    <Box fontSize='11px' fontWeight='semibold' letterSpacing='.06em' textTransform='uppercase' color='fg.subtle'>
      {props.title}
    </Box>
    {props.children}
  </Grid>
)

/** Single-choice segmented control that wraps on narrow screens */
export function ChoiceGroup<T extends string>(props: {
  label: string
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <Field.Root>
      <Field.Label>{props.label}</Field.Label>
      <Flex role='radiogroup' aria-label={props.label} class={segTrack} flexWrap='wrap' w='fit-content' maxW='full'>
        <For each={props.options}>
          {(o) => (
            <button
              type='button'
              role='radio'
              aria-checked={props.value === o.value}
              class={segButton(props.value === o.value)}
              onClick={() => props.onChange(o.value)}
            >
              {o.label}
            </button>
          )}
        </For>
      </Flex>
    </Field.Root>
  )
}

const chip = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  h: '7',
  pl: '2.5',
  pr: '1.5',
  borderRadius: 'full',
  borderWidth: '1px',
  borderColor: 'jade.7',
  bg: 'jade.a2',
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'fg.default',
  whiteSpace: 'nowrap',
  '& button': {
    display: 'inline-flex',
    color: 'fg.subtle',
    cursor: 'pointer',
    borderRadius: 'full',
    p: '0.5',
    _hover: { color: 'fg.default', bg: 'jade.a4' },
  },
})

/** Removable chip summarising one active filter */
export const ActiveChip = (props: { children: JSX.Element; onRemove: () => void; label: string }) => (
  <span class={chip}>
    {props.children}
    <button type='button' aria-label={`Remove ${props.label}`} onClick={props.onRemove}>
      <X size={12} />
    </button>
  </span>
)

export const ClearAllButton = (props: { onClick: () => void }) => (
  <button
    type='button'
    class={css({
      fontSize: 'xs',
      color: 'fg.subtle',
      cursor: 'pointer',
      px: '1.5',
      _hover: { color: 'fg.default', textDecoration: 'underline' },
    })}
    onClick={props.onClick}
  >
    Clear all
  </button>
)
